// setupAxios.js — instancia de axios con JWT (réplica del otro proyecto).
//
//  - baseURL desde REACT_APP_API_URL (por defecto "/api", relativo al propio
//    dominio: en local lo resuelve el "proxy" de package.json, en producción
//    lo resuelve nginx). Nunca debe apuntar a localhost: eso sería el
//    localhost del navegador de quien visita la página, no el del servidor.
//  - Interceptor de petición: añade "Authorization: Bearer <access_token>".
//  - Interceptor de respuesta: ante un 401 intenta refrescar con el
//    refresh_token contra /login/refresh; encola las peticiones concurrentes
//    mientras se refresca; si el refresh falla, limpia la sesión y va a /login.
import axios from "axios";

export const API_URL = process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// ---- almacenamiento de tokens en localStorage ----
export const tokenStore = {
  get access() {
    return localStorage.getItem("access_token");
  },
  get refresh() {
    return localStorage.getItem("refresh_token");
  },
  set(access, refresh) {
    if (access) localStorage.setItem("access_token", access);
    if (refresh) localStorage.setItem("refresh_token", refresh);
  },
  clear() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
  },
};

// ---- petición: añade el access token ----
api.interceptors.request.use((config) => {
  const t = tokenStore.access;
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// ---- respuesta: refresh en 401 con cola de peticiones ----
let refreshing = false;
let cola = [];

function resolverCola(error, token = null) {
  cola.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  cola = [];
}

function cerrarSesion() {
  tokenStore.clear();
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const original = error.config;
    const status = error.response ? error.response.status : null;
    const esLogin =
      original && (original.url || "").includes("/login");

    if (status === 401 && original && !original._retry && !esLogin) {
      if (refreshing) {
        // ya se está refrescando: encola esta petición
        return new Promise((resolve, reject) => {
          cola.push({ resolve, reject });
        })
          .then((token) => {
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
          })
          .catch((e) => Promise.reject(e));
      }

      original._retry = true;
      refreshing = true;

      return new Promise((resolve, reject) => {
        axios
          .post(
            `${API_URL}/login/refresh`,
            {},
            {
              headers: { Authorization: `Bearer ${tokenStore.refresh}` },
              withCredentials: true,
            }
          )
          .then(({ data }) => {
            const nuevoAccess = data.access_token;
            const nuevoRefresh = data.refresh_token || tokenStore.refresh;
            tokenStore.set(nuevoAccess, nuevoRefresh);
            api.defaults.headers.common.Authorization = `Bearer ${nuevoAccess}`;
            original.headers.Authorization = `Bearer ${nuevoAccess}`;
            resolverCola(null, nuevoAccess);
            resolve(api(original));
          })
          .catch((e) => {
            resolverCola(e, null);
            cerrarSesion();
            reject(e);
          })
          .finally(() => {
            refreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);

export default api;
